/*
 * Resources: every guide, in the order the Guides page lists them. A guide is
 * a list of Markdown chapters (one file each, imported raw) with its
 * audiences and an optional PDF printed from /guides/:guide/print
 * (apps/web/scripts/render-guide-pdf.sh). The organization guides share
 * their capability chapters (chapters/), with notes per audience.
 */
import { organizationsGuide } from './organizations/guide';
import { universitiesGuide } from './universities/guide';
import { nonprofitsGuide } from './nonprofits/guide';
import { companiesGuide } from './companies/guide';
import { communitiesGuide } from './communities/guide';
import { learnersGuide } from './learners/guide';
import { authorsGuide } from './authors/guide';
import { adminsGuide } from './admins/guide';
import type { Guide, GuideAudience, GuideChapter } from './types';

export type { Guide, GuideAudience, GuideChapter } from './types';

export const GUIDES: Guide[] = [
  organizationsGuide,
  universitiesGuide,
  nonprofitsGuide,
  companiesGuide,
  communitiesGuide,
  learnersGuide,
  authorsGuide,
  adminsGuide,
];

export const AUDIENCE_LABELS: Record<GuideAudience, string> = {
  organizations: 'Organizations',
  universities: 'Universities',
  nonprofits: 'Non-profits',
  communities: 'Communities',
  companies: 'Companies',
  learners: 'Learners',
  authors: 'Course authors',
  admins: 'Admins',
};

/** Who the guides for learners, authors and admins are for; every other audience is a kind of organization. */
const ROLE_AUDIENCES: GuideAudience[] = ['learners', 'authors', 'admins'];

export const isOrganizationGuide = (g: Guide) =>
  g.audiences.some((a) => !ROLE_AUDIENCES.includes(a));

export function findGuide(slug: string | undefined): Guide | undefined {
  return GUIDES.find((g) => g.slug === slug);
}

export const guidePath = (guide: string, chapter?: string) =>
  chapter ? `/guides/${guide}/${chapter}` : `/guides/${guide}`;

export interface SearchHit {
  guide: Guide;
  chapter: GuideChapter;
  /** A short excerpt around the first match in the chapter's text. */
  excerpt: string;
}

/** Readable text of a chapter: no images, link targets, directives or Markdown marks. */
function plainText(markdown: string): string {
  return markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^:{2,}.*$/gm, ' ')
    .replace(/[#>*_`|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const texts = new Map<GuideChapter, string>();
const textOf = (c: GuideChapter) => {
  let t = texts.get(c);
  if (t === undefined) {
    t = plainText(c.markdown);
    texts.set(c, t);
  }
  return t;
};

/**
 * Chapters matching every word of `query`, best first: words in the title
 * count most, then the summary, then the body.
 */
export function searchGuides(query: string, limit = 12): SearchHit[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const hits: (SearchHit & { score: number })[] = [];
  for (const guide of GUIDES) {
    for (const chapter of guide.chapters) {
      const title = chapter.title.toLowerCase();
      const summary = chapter.summary.toLowerCase();
      const body = textOf(chapter);
      const lower = body.toLowerCase();
      let score = 0;
      let ok = true;
      for (const w of words) {
        const s =
          (title.includes(w) ? 6 : 0) + (summary.includes(w) ? 3 : 0) + (lower.includes(w) ? 1 : 0);
        if (!s) {
          ok = false;
          break;
        }
        score += s;
      }
      if (!ok) continue;
      const at = Math.max(0, lower.indexOf(words[0]!));
      const from = Math.max(0, at - 60);
      const excerpt =
        (from > 0 ? '…' : '') +
        body.slice(from, from + 180).trim() +
        (from + 180 < body.length ? '…' : '');
      hits.push({ guide, chapter, excerpt, score });
    }
  }
  // Shared chapters appear in every organization guide: keep each once, from
  // the guide where it matched best (the general guide on a tie).
  const seen = new Set<string>();
  return hits
    .sort((a, b) => b.score - a.score)
    .filter((h) => {
      const key = `${h.chapter.slug}:${h.chapter.title}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit);
}
